import React, { useEffect, useRef, useState } from 'react';
import { TravelMode } from '../types';
import { getRoute } from '../services/routexApi';

export interface TripConfig {
  travelMode: TravelMode;
  radius: number; // in km
  maxTime: number; // in minutes
  numberOfPeople: number;
  startAddress?: string;
  endAddress?: string;
}

interface TripConfigurationProps {
  onComplete: (config: TripConfig) => void;
  onSetAddressesOnMap?: (configDraft: TripConfig) => void;
  onBack: () => void;
  initialConfig?: TripConfig;
}

const TRAVEL_MODES = [
  { value: 'DRIVING' as TravelMode, label: 'Car', icon: 'directions_car' },
  { value: 'BICYCLING' as TravelMode, label: 'Bike', icon: 'directions_bike' },
  { value: 'WALKING' as TravelMode, label: 'Walk', icon: 'directions_walk' },
  { value: 'TRANSIT' as TravelMode, label: 'Public Transport', icon: 'directions_transit' },
];

// Slider configuration
const RADIUS_SLIDER = {
  min: 1,
  max: 100,
  step: 1,
  marks: [1, 5, 10, 20, 30, 50, 75, 100],
};

const TIME_SLIDER = {
  min: 15,
  max: 960,
  step: 15,
  marks: [60, 120, 240, 480, 720, 960],
};

// Helper to format time for display
const formatTime = (minutes: number): string => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
  return `${hours}h ${mins}m`;
};

// Compact label for slider marks (m/h only).
const formatTimeCompact = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

export const TripConfiguration: React.FC<TripConfigurationProps> = ({
  onComplete,
  onSetAddressesOnMap,
  onBack,
  initialConfig,
}) => {
  const [travelMode, setTravelMode] = useState<TravelMode>(initialConfig?.travelMode || 'DRIVING');
  const [numberOfPeople, setNumberOfPeople] = useState<number>(initialConfig?.numberOfPeople ?? 1);
  const [radius, setRadius] = useState<number>(initialConfig?.radius || 5); // default 5 km
  const [maxTime, setMaxTime] = useState<number>(initialConfig?.maxTime || 120); // default 2 hours
  const [isEstimatingFastest, setIsEstimatingFastest] = useState(false);
  const [fastestEstimateMinutes, setFastestEstimateMinutes] = useState<number | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [showAddressWarningModal, setShowAddressWarningModal] = useState(false);
  const lastEstimatedRouteKeyRef = useRef<string>('');
  const startAddress = (initialConfig?.startAddress || '').trim();
  const endAddress = (initialConfig?.endAddress || '').trim();
  const effectiveMinTime = Math.max(TIME_SLIDER.min, fastestEstimateMinutes ?? TIME_SLIDER.min);
  const timeRangeSpan = Math.max(1, TIME_SLIDER.max - effectiveMinTime);
  const timeMarks = Array.from(new Set([effectiveMinTime, ...TIME_SLIDER.marks]))
    .filter((m) => m >= effectiveMinTime)
    .sort((a, b) => a - b);
  const canContinue =
    !!startAddress &&
    !!endAddress &&
    !isEstimatingFastest &&
    fastestEstimateMinutes != null;

  const buildDraftConfig = (): TripConfig => {
    return {
      travelMode,
      radius,
      maxTime,
      numberOfPeople,
      startAddress: initialConfig?.startAddress,
      endAddress: initialConfig?.endAddress,
    };
  };

  useEffect(() => {
    const start = startAddress;
    const end = endAddress;
    if (!start || !end) {
      lastEstimatedRouteKeyRef.current = '';
      setFastestEstimateMinutes(null);
      setEstimateError(null);
      return;
    }
    const routeKey = `${start.toLowerCase()}__${end.toLowerCase()}__${travelMode}`;
    if (lastEstimatedRouteKeyRef.current === routeKey && fastestEstimateMinutes != null) return;

    let cancelled = false;
    // Travel mode changed (or route changed): clear stale estimate immediately
    // so UI always reflects the currently selected mode while recomputing.
    if (lastEstimatedRouteKeyRef.current !== routeKey) {
      setFastestEstimateMinutes(null);
    }
    setIsEstimatingFastest(true);
    setEstimateError(null);

    (async () => {
      try {
        const route = await getRoute({
          start,
          end,
          mode: travelMode,
          alternative: false,
        });
        if (cancelled) return;
        const sameAddress =
          start.trim().toLowerCase() === end.trim().toLowerCase();
        const seconds = (route.legs || []).reduce((sum, leg) => sum + (leg.duration?.value || 0), 0);
        if (seconds < 0 || (!sameAddress && seconds === 0)) {
          setEstimateError('Could not estimate fastest route yet. Try refining the addresses.');
          return;
        }
        const mins = sameAddress ? 0 : Math.max(1, Math.round(seconds / 60));
        lastEstimatedRouteKeyRef.current = routeKey;
        setFastestEstimateMinutes(mins);
        // Enforce floor: user max travel time cannot be below fastest route estimate.
        setMaxTime((prev) => (prev < mins ? mins : prev));
      } catch {
        if (!cancelled) {
          setEstimateError('Failed to estimate fastest route. Please verify addresses.');
        }
      } finally {
        if (!cancelled) {
          setIsEstimatingFastest(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [startAddress, endAddress, travelMode, fastestEstimateMinutes]);

  const handleContinue = () => {
    const start = startAddress;
    const end = endAddress;

    if (!start || !end) {
      setShowAddressWarningModal(true);
      return;
    }

    if (isNaN(radius) || radius <= 0) {
      alert('Please enter a valid radius in km');
      return;
    }

    if (isNaN(maxTime) || maxTime <= 0) {
      alert('Please enter a valid maximum travel time in minutes');
      return;
    }
    if (fastestEstimateMinutes != null && maxTime < fastestEstimateMinutes) {
      alert(`Maximum travel time must be at least ${formatTime(fastestEstimateMinutes)} (fastest route estimate).`);
      return;
    }

    onComplete({
      travelMode,
      radius,
      maxTime,
      numberOfPeople,
      startAddress: start,
      endAddress: end,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col p-4">
      {/* Header */}
      <div className="mb-6 mt-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors mb-4"
        >
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          <span className="text-sm font-medium">Back</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Configure Your Trip</h1>
        <p className="text-lg text-gray-600">Choose your travel preferences</p>
      </div>

      <div className="flex-1 overflow-y-auto pb-4">
        <div className="max-w-md mx-auto w-full space-y-6">
          {/* Travel Mode Selection */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Travel Mode</h2>
            <div className="grid grid-cols-2 gap-3">
              {TRAVEL_MODES.map((mode) => (
                <button
                  key={mode.value}
                  onClick={() => setTravelMode(mode.value)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    travelMode === mode.value
                      ? 'border-blue-600 bg-blue-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <span
                      className={`material-symbols-outlined text-3xl ${
                        travelMode === mode.value ? 'text-blue-600' : 'text-gray-600'
                      }`}
                      style={{ fontVariationSettings: "'FILL' 1" }}
                    >
                      {mode.icon}
                    </span>
                    <span
                      className={`text-sm font-medium ${
                        travelMode === mode.value ? 'text-blue-900' : 'text-gray-700'
                      }`}
                    >
                      {mode.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Addresses + automatic fastest estimate */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Addresses</h2>
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm space-y-4">
              {startAddress && endAddress ? (
                <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 to-indigo-50 p-3">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-900 break-words">{startAddress}</p>
                    <p className="text-center text-gray-400">↓</p>
                    <p className="text-sm font-medium text-gray-900 break-words">{endAddress}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                  Choose your start/destination addresses on the map
                </p>
              )}
              <button
                type="button"
                onClick={() => onSetAddressesOnMap?.(buildDraftConfig())}
                className="w-full rounded-xl bg-gray-900 text-white py-2.5 font-medium hover:bg-black transition-colors"
              >
                {startAddress && endAddress ? 'Update Addresses on Map' : 'Select Addresses on Map'}
              </button>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                {isEstimatingFastest ? (
                  <p className="text-sm text-gray-600">Estimating fastest route time...</p>
                ) : fastestEstimateMinutes != null ? (
                  <p className="text-sm text-green-700">
                    Fastest route estimate: <span className="font-semibold">{formatTime(fastestEstimateMinutes)}</span>. Maximum travel time cannot be lower than this value.
                  </p>
                ) : (
                  <p className="text-sm text-gray-600">Fastest route estimate will appear automatically after addresses are set.</p>
                )}
                {estimateError && <p className="text-sm text-red-600 mt-1">{estimateError}</p>}
              </div>
            </div>
          </div>

          {/* How many travelers */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">How many travelers?</h2>
            <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">People</p>
                <p className="text-xs text-gray-400">Use + / - to adjust</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setNumberOfPeople((prev) => Math.max(1, prev - 1))}
                  className="h-10 w-10 rounded-full border-2 border-gray-200 text-gray-700 hover:border-blue-300 hover:text-blue-600 transition-colors text-xl leading-none"
                >
                  -
                </button>
                <div className="h-14 w-14 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl font-bold shadow-md">
                  {numberOfPeople}
                </div>
                <button
                  type="button"
                  onClick={() => setNumberOfPeople((prev) => Math.min(10, prev + 1))}
                  className="h-10 w-10 rounded-full border-2 border-gray-200 text-gray-700 hover:border-blue-300 hover:text-blue-600 transition-colors text-xl leading-none"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {/* Radius Selection */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Search Radius</h2>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-blue-600">{radius}</span>
                <span className="text-gray-500 font-medium">km</span>
              </div>
            </div>

            {/* Slider Container */}
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm transition-all">
              {/* Slider Track */}
              <div className="relative pt-2 pb-6">
                <input
                  type="range"
                  min={RADIUS_SLIDER.min}
                  max={RADIUS_SLIDER.max}
                  step={RADIUS_SLIDER.step}
                  value={radius}
                  onChange={(e) => setRadius(parseInt(e.target.value))}
                  className="w-full h-2 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full appearance-none cursor-pointer disabled:cursor-not-allowed
                    [&::-webkit-slider-thumb]:appearance-none
                    [&::-webkit-slider-thumb]:w-6
                    [&::-webkit-slider-thumb]:h-6
                    [&::-webkit-slider-thumb]:rounded-full
                    [&::-webkit-slider-thumb]:bg-gradient-to-br
                    [&::-webkit-slider-thumb]:from-blue-500
                    [&::-webkit-slider-thumb]:to-indigo-600
                    [&::-webkit-slider-thumb]:shadow-lg
                    [&::-webkit-slider-thumb]:shadow-blue-500/30
                    [&::-webkit-slider-thumb]:border-4
                    [&::-webkit-slider-thumb]:border-white
                    [&::-webkit-slider-thumb]:transition-transform
                    [&::-webkit-slider-thumb]:hover:scale-110
                    [&::-webkit-slider-thumb]:active:scale-95
                    [&::-moz-range-thumb]:w-6
                    [&::-moz-range-thumb]:h-6
                    [&::-moz-range-thumb]:rounded-full
                    [&::-moz-range-thumb]:bg-gradient-to-br
                    [&::-moz-range-thumb]:from-blue-500
                    [&::-moz-range-thumb]:to-indigo-600
                    [&::-moz-range-thumb]:shadow-lg
                    [&::-moz-range-thumb]:border-4
                    [&::-moz-range-thumb]:border-white
                    [&::-moz-range-thumb]:cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #6366f1 ${((radius - RADIUS_SLIDER.min) / (RADIUS_SLIDER.max - RADIUS_SLIDER.min)) * 100}%, #e0e7ff ${((radius - RADIUS_SLIDER.min) / (RADIUS_SLIDER.max - RADIUS_SLIDER.min)) * 100}%, #e0e7ff 100%)`
                  }}
                />

                {/* Marks */}
                <div className="relative mt-3 px-1">
                  {RADIUS_SLIDER.marks.map((mark) => {
                    const position = ((mark - RADIUS_SLIDER.min) / (RADIUS_SLIDER.max - RADIUS_SLIDER.min)) * 100;
                    return (
                      <button
                        key={mark}
                        onClick={() => setRadius(mark)}
                        className={`absolute text-xs font-medium transition-colors transform -translate-x-1/2 ${
                          radius === mark
                            ? 'text-blue-600'
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                        style={{ left: `${position}%` }}
                      >
                        {mark}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Maximum Travel Time Selection */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Maximum Travel Time</h2>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-indigo-600">{formatTime(maxTime)}</span>
              </div>
            </div>

            {/* Slider Container */}
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm transition-all">
              {/* Slider Track */}
              <div className="relative pt-2 pb-6">
                <input
                  type="range"
                  min={effectiveMinTime}
                  max={TIME_SLIDER.max}
                  step={TIME_SLIDER.step}
                  value={Math.max(maxTime, effectiveMinTime)}
                  onChange={(e) => setMaxTime(parseInt(e.target.value))}
                  className="w-full h-2 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full appearance-none cursor-pointer disabled:cursor-not-allowed
                    [&::-webkit-slider-thumb]:appearance-none
                    [&::-webkit-slider-thumb]:w-6
                    [&::-webkit-slider-thumb]:h-6
                    [&::-webkit-slider-thumb]:rounded-full
                    [&::-webkit-slider-thumb]:bg-gradient-to-br
                    [&::-webkit-slider-thumb]:from-indigo-500
                    [&::-webkit-slider-thumb]:to-purple-600
                    [&::-webkit-slider-thumb]:shadow-lg
                    [&::-webkit-slider-thumb]:shadow-indigo-500/30
                    [&::-webkit-slider-thumb]:border-4
                    [&::-webkit-slider-thumb]:border-white
                    [&::-webkit-slider-thumb]:transition-transform
                    [&::-webkit-slider-thumb]:hover:scale-110
                    [&::-webkit-slider-thumb]:active:scale-95
                    [&::-moz-range-thumb]:w-6
                    [&::-moz-range-thumb]:h-6
                    [&::-moz-range-thumb]:rounded-full
                    [&::-moz-range-thumb]:bg-gradient-to-br
                    [&::-moz-range-thumb]:from-indigo-500
                    [&::-moz-range-thumb]:to-purple-600
                    [&::-moz-range-thumb]:shadow-lg
                    [&::-moz-range-thumb]:border-4
                    [&::-moz-range-thumb]:border-white
                    [&::-moz-range-thumb]:cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #6366f1 0%, #9333ea ${((Math.max(maxTime, effectiveMinTime) - effectiveMinTime) / timeRangeSpan) * 100}%, #e0e7ff ${((Math.max(maxTime, effectiveMinTime) - effectiveMinTime) / timeRangeSpan) * 100}%, #e0e7ff 100%)`
                  }}
                />

                {/* Marks */}
                <div className="relative mt-3 px-1">
                  {timeMarks.map((mark) => {
                    const position = ((mark - effectiveMinTime) / timeRangeSpan) * 100;
                    return (
                      <button
                        key={mark}
                        onClick={() => setMaxTime(mark)}
                        className={`absolute text-xs font-medium transition-colors transform -translate-x-1/2 ${
                          maxTime === mark
                            ? 'text-indigo-600'
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                        style={{ left: `${position}%` }}
                      >
                        {formatTimeCompact(mark)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Continue Button */}
      <div className="pt-4 pb-safe-bottom">
        <button
          onClick={handleContinue}
          disabled={!canContinue}
          className={`w-full max-w-md mx-auto rounded-xl py-4 font-semibold text-lg transition-all shadow-lg flex items-center justify-center gap-2 ${
            canContinue
              ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 hover:shadow-xl'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
          }`}
        >
          <span>Continue</span>
          <span className="material-symbols-outlined">arrow_forward</span>
        </button>
      </div>

      {/* Missing address warning modal */}
      {showAddressWarningModal && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowAddressWarningModal(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
              <span className="material-symbols-outlined text-white text-3xl">warning</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 text-center mb-2">Addresses Required</h3>
            <p className="text-sm text-gray-600 text-center mb-5">
              Please choose your start and destination addresses on the map before continuing.
            </p>
            <button
              type="button"
              onClick={() => setShowAddressWarningModal(false)}
              className="w-full rounded-xl bg-blue-600 text-white py-2.5 font-medium hover:bg-blue-700 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
